import {
  CreatedSalesFactor,
  CreatedSalesPrice,
  DatedPackageMarkups,
  EditBracketPricesDialog,
  useBracketPackagePrices,
  useBracketPriceStore,
} from '@retail/my-assortment/components';
import { useProductsTFunction } from '@retail/products/i18n';
import { useCallback } from 'react';
import { PackageCalculation } from '@retail/my-assortment/types';
import { useConditionActions } from '@retail/calculus/context';
import { CreateCalculusConditions } from '@retail/calculus/types';
import { getISO_8601Date } from '@retail/utils';

interface Props {
  supplierItemPackages: PackageCalculation[];
  basePriceUnit: string;
  selectedSupplierId: number;
  mgItemNumber: number;
  includeVat: boolean;
  toggleIncludeVat: () => void;
  isBracketPriceModalOpen: boolean;
  onCloseBracketPriceModal: () => void;
}

export function EditBracketPricesDialogContainer({
  supplierItemPackages,
  basePriceUnit,
  selectedSupplierId,
  mgItemNumber,
  includeVat,
  toggleIncludeVat,
  isBracketPriceModalOpen,
  onCloseBracketPriceModal,
}: Props) {
  const t = useProductsTFunction();
  const { resetMarkup } = useBracketPriceStore((state) => state);

  const { createMultipleConditions, isLoadingPostChanges } = useConditionActions();

  const packagePrices = useBracketPackagePrices(supplierItemPackages, includeVat);

  const closeModalAndResetMarkup = useCallback(() => {
    onCloseBracketPriceModal();
    resetMarkup();
  }, [onCloseBracketPriceModal, resetMarkup]);

  const saveBracketPrices = useCallback(
    ({ packageMarkups, validFrom }: DatedPackageMarkups) => {
      const markups: CreateCalculusConditions[] = Object.values(packageMarkups)
        .map((packageMarkup) => {
          return packageMarkup
            ? {
                content: {
                  markup: (packageMarkup as CreatedSalesFactor)?.salesFactor
                    ? { salesFactor: (packageMarkup as CreatedSalesFactor).salesFactor }
                    : undefined,
                  markupTargetPrice: (packageMarkup as CreatedSalesPrice)?.salesPriceIncVat
                    ? {
                        targetPrice: includeVat
                          ? (packageMarkup as CreatedSalesPrice).salesPriceIncVat
                          : (packageMarkup as CreatedSalesPrice).salesPriceWithoutVat,
                        incMva: includeVat,
                        unit: basePriceUnit,
                      }
                    : undefined,
                },
                binding: {
                  legacySupplierItem: {
                    mgItemNumber,
                    supplierNumber: selectedSupplierId,
                    gtinCode: packageMarkup.gtin,
                  },
                },
                validFrom: getISO_8601Date(validFrom),
              }
            : undefined;
        })
        .filter((markup) => !!markup);
      return createMultipleConditions(markups);
    },

    [createMultipleConditions, includeVat, mgItemNumber, selectedSupplierId, basePriceUnit]
  );

  const onSubmitBracketPrices = useCallback(
    (markups: DatedPackageMarkups) => {
      saveBracketPrices(markups);
      closeModalAndResetMarkup();
    },
    [closeModalAndResetMarkup, saveBracketPrices]
  );
  return (
    <EditBracketPricesDialog
      open={isBracketPriceModalOpen}
      onClose={closeModalAndResetMarkup}
      includeVat={includeVat}
      onToggleIncludeVat={toggleIncludeVat}
      saveMarkups={onSubmitBracketPrices}
      isSavingMarkups={isLoadingPostChanges}
      packagePrices={packagePrices}
      baseUnit={basePriceUnit}
      editable
      t={t}
    />
  );
}
